import { ProductBadge } from '@app/components/various/ProductBadge';
import { getIsDefaultBadge } from '@app/ducks/helpers';
import { cn } from '@app/utils/cn';
import { useDebouncedCallback } from '@app/utils/hooks';
import { useLookbookProductBadges } from '@app/utils/hooks/useLookbookProductBadges';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';

const BADGES_SPACING = 12;
interface Props {
  badgeSize?: 'small' | 'medium';
  spacing?: 'small' | 'medium';
  variant: Product.Standard;
  overlapHeight?: number;
  parentWidth?: number;
}

export const LookbookProductBadges = React.memo(
  ({ variant, badgeSize = 'medium', spacing = 'medium', overlapHeight = 0, parentWidth }: Props) => {
    const { badgesCount, customBadges, defaultBadges } = useLookbookProductBadges({
      variant,
    });

    const badgesRef = React.useRef<HTMLDivElement>(null);
    const [fittingBadgeCount, setFittingBadgeCount] = React.useState(badgesCount);
    const calculateFittingBadges = React.useCallback(() => {
      const wrapperEl = badgesRef.current;
      const badgeEl = wrapperEl?.children[0];

      if (!isDefined(wrapperEl) || !isDefined(badgeEl)) {
        return;
      }

      setFittingBadgeCount(Math.floor((wrapperEl.clientHeight - overlapHeight) / (badgeEl.clientHeight + BADGES_SPACING / 2)) - 1);
    }, [overlapHeight]);

    const resizeCallback = useDebouncedCallback(calculateFittingBadges);
    React.useEffect(() => {
      resizeCallback();
    }, [parentWidth, resizeCallback]);
    const badgesToShow = [...defaultBadges, ...customBadges].slice(0, fittingBadgeCount);

    if (isEmpty(badgesToShow)) {
      return null;
    }

    return (
      <div
        ref={badgesRef}
        className={cn('relative z-elevated flex h-full flex-col gap-3 overflow-hidden', { 'gap-2': spacing === 'small' })}
      >
        {badgesToShow.map(badge => {
          const isDefaultBadge = getIsDefaultBadge(badge);
          const key = isDefaultBadge ? badge.badgeType : badge.name;

          return (
            <div key={key} className="max-w-full align-top">
              <ProductBadge badge={badge} size={badgeSize} />
            </div>
          );
        })}
      </div>
    );
  },
);
